import { useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchPlans,
  fetchUserSubscription,
  fetchUserQuota,
  changePlan,
  startFreeSubscription,
  startTrial,
  upgradeSubscription,
  cancelSubscription,
  fetchFeatureConfig,
  updateFeatureConfig,
  startBillingSubscription,
  cancelBillingSubscription,
  setBillingAutoRenew,
  fetchAdminUsers,
  clearErrors,
  clearSubscription,
  updateQuotaWithSubscription,
  updateCreditsUsage,
  refreshSubscriptionData,
  resetQuotaToFreeTier
} from '../store/subscriptionSlice';
import { createSubscriptionPoller } from '../utils/subscriptionUtils';
import subscriptionService from '../services/subscriptionService';

export const useSubscription = () => {
  const dispatch = useDispatch();
  const {
    plans,
    currentSubscription,
    currentQuota,
    featureConfig,
    loading,
    plansLoading,
    subscriptionLoading,
    quotaLoading,
    error,
    adminUsers,
    adminLoading,
    adminError,
    billingLoading,
    billingError,
    subscriptionRefreshTrigger
  } = useSelector((state) => state.subscription);

  const { user } = useSelector((state) => state.auth);
  
  // Track if we've already attempted to load subscription to prevent infinite loops
  const hasAttemptedSubscriptionLoad = useRef(false);

  // Fetch available plans (memoized to prevent unnecessary calls)
  const loadPlans = useCallback(() => {
    dispatch(fetchPlans());
  }, [dispatch]); // Only depend on dispatch which is stable

  // Fetch user's current subscription (memoized to prevent unnecessary calls)
  // Only call when explicitly needed - not automatically for all users
  const loadUserSubscription = useCallback(async () => {
    if (user?.id) {
      return await dispatch(fetchUserSubscription(user.id));
    }
  }, [dispatch, user?.id]); // Only depend on stable values

  // Load quota data for a user
  const loadUserQuota = useCallback(async () => {
    if (user?.id) {
      return await dispatch(fetchUserQuota(user.id));
    }
  }, [dispatch, user?.id]); // Only depend on stable values

  // Update quota data with subscription purchase credits
  const updateQuotaWithSubscriptionData = useCallback(() => {
    dispatch(updateQuotaWithSubscription());
  }, [dispatch]);

  // Update credits usage when a purchase is made
  const updateCreditsUsageData = useCallback((creditsUsed) => {
    dispatch(updateCreditsUsage({ creditsUsed }));
  }, [dispatch]);

  // Automatically load subscription data when user is available
  useEffect(() => {
    if (user?.id && !subscriptionLoading && !hasAttemptedSubscriptionLoad.current) {
      hasAttemptedSubscriptionLoad.current = true;
      loadUserSubscription();
      loadPlans(); // Also load plans
      loadUserQuota(); // Also load quota data
    }
    
    // Reset the flag if user changes
    if (!user?.id) {
      hasAttemptedSubscriptionLoad.current = false;
    }
  }, [user?.id, subscriptionLoading, loadUserSubscription, loadPlans, loadUserQuota]); // Include dependencies

  // Update quota data when subscription data changes
  useEffect(() => {
    if (currentSubscription && currentQuota) {
      updateQuotaWithSubscriptionData();
    }
  }, [currentSubscription, currentQuota, updateQuotaWithSubscriptionData]);

  // Log subscription state changes
  useEffect(() => {
    // Subscription state logging removed for production
    console.log('Subscription state changed:', {
      plans,
      currentSubscription,
      currentQuota,
      plansLoading,
      subscriptionLoading,
      quotaLoading,
      error
    });
  }, [plans, currentSubscription, currentQuota, plansLoading, subscriptionLoading, quotaLoading, error]);

  // Check if user has an active subscription (for conditional loading)
  const hasActiveSubscription = useCallback(() => {
    return currentSubscription && currentSubscription.status === 'ACTIVE';
  }, [currentSubscription]); // Include currentSubscription as dependency

  // Load subscription only when we're sure user has one (e.g., after successful subscription creation)
  const loadSubscriptionIfExists = useCallback(() => {
    if (user?.id) {
      dispatch(fetchUserSubscription(user.id));
    }
  }, [dispatch, user?.id]); // Only depend on stable values

  // Load subscription data when explicitly needed (e.g., for subscription management pages)
  const loadSubscriptionForManagement = useCallback(() => {
    if (user?.id) {
      dispatch(fetchUserSubscription(user.id));
    }
  }, [dispatch, user?.id]); // Only depend on stable values

  // Refresh all subscription data (subscription + quota + plans)
  const refreshSubscriptionData = useCallback(async (usePolling = false) => {
    if (user?.id) {
      if (usePolling) {
        // Use polling for subscription data to wait for webhook processing
        try {
          console.log('🔄 Refreshing subscription data with polling...');
          
          // Wait a moment for webhook to process
          await new Promise(resolve => setTimeout(resolve, 2000));
          
          const finalSubscription = await createSubscriptionPoller(
            () => subscriptionService.getUserSubscription(user.id).then(res => res.data),
            15, // max attempts (30 seconds total)
            2000 // 2 second intervals
          );
          
          if (finalSubscription && finalSubscription.status === 'ACTIVE') {
            console.log('✅ Subscription polling successful, refreshing all data');
            // Refresh quota and plans after successful subscription polling
            dispatch(fetchUserQuota(user.id));
            dispatch(fetchPlans());
          } else {
            console.log('⚠️ Subscription polling completed but status not ACTIVE, doing regular refresh');
            // Fallback to regular refresh
            dispatch(fetchUserSubscription(user.id));
            dispatch(fetchUserQuota(user.id));
            dispatch(fetchPlans());
          }
        } catch (error) {
          console.log('❌ Subscription polling failed, doing regular refresh:', error.message);
          // Fallback to regular refresh
          dispatch(fetchUserSubscription(user.id));
          dispatch(fetchUserQuota(user.id));
          dispatch(fetchPlans());
        }
      } else {
        // Regular refresh without polling
        dispatch(fetchUserSubscription(user.id));
        dispatch(fetchUserQuota(user.id));
        dispatch(fetchPlans());
      }
    }
  }, [dispatch, user?.id]);

  // Smart refresh that detects if polling might be needed
  const smartRefreshSubscriptionData = useCallback(async () => {
    if (user?.id) {
      // Check if we have a subscription that might be processing
      const hasProcessingSubscription = currentSubscription && 
        (currentSubscription.status === 'PENDING' || 
         currentSubscription.status === 'DEFAULT_INCOMPLETE');
      
      // Use polling if we detect a processing subscription or if we don't have subscription data
      const usePolling = hasProcessingSubscription || !currentSubscription;
      
      console.log('🧠 Smart refresh - usePolling:', usePolling, 'hasProcessingSubscription:', hasProcessingSubscription);
      
      await refreshSubscriptionData(usePolling);
    }
  }, [user?.id, currentSubscription, refreshSubscriptionData]);

  // Check if subscription data should be loaded based on context
  const shouldLoadSubscription = useCallback((context) => {
    // Only load subscription data in specific contexts where we know it's needed
    const contextsThatNeedSubscription = [
      'subscription-management',
      'billing',
      'feature-gating',
      'post-subscription-creation'
    ];
    
    return contextsThatNeedSubscription.includes(context);
  }, []);

  // Trigger immediate subscription data refresh
  const triggerSubscriptionRefresh = useCallback(async () => {
    dispatch(refreshSubscriptionData());
    if (user?.id) {
      // Immediately fetch fresh data using async thunks
      await Promise.all([
        dispatch(fetchUserSubscription(user.id)),
        dispatch(fetchUserQuota(user.id))
      ]);
    }
  }, [dispatch, user?.id]);

  // Update quota data with current subscription plan information
  const syncQuotaWithSubscription = useCallback(() => {
    dispatch(updateQuotaWithSubscription());
  }, [dispatch]);

  // Reset quota to free tier (for cancelled subscriptions)
  const resetQuotaToFree = useCallback(() => {
    dispatch(resetQuotaToFreeTier());
  }, [dispatch]);

  // Change user's plan
  const handleChangePlan = useCallback(async (planCode) => {
    if (user?.id) {
      const result = await dispatch(changePlan({ userId: user.id, planCode }));
      // Trigger immediate refresh after successful plan change
      if (result.type.endsWith('/fulfilled')) {
        await triggerSubscriptionRefresh();
      }
      return result;
    }
  }, [dispatch, user?.id, triggerSubscriptionRefresh]);

  // Start a free subscription
  const handleStartFreeSubscription = useCallback(async ({ userId, planCode = 'FREE' }) => {
    const result = await dispatch(startFreeSubscription({ userId, planCode }));
    // Trigger immediate refresh after successful subscription creation
    if (result.type.endsWith('/fulfilled')) {
      await triggerSubscriptionRefresh();
    }
    return result;
  }, [dispatch, triggerSubscriptionRefresh]);

  // Start a paid subscription (admin only)
  const handleStartBillingSubscription = useCallback((billingData) => {
    return dispatch(startBillingSubscription(billingData));
  }, [dispatch]);

  // Note: Auto-renew functionality requires stripe subscription ID
  // Use handleSetBillingAutoRenew(stripeSubId, enabled) instead

  // Admin actions
  const handleStartTrial = useCallback((userId, planCode) => {
    return dispatch(startTrial({ userId, planCode }));
  }, [dispatch]);

  const handleUpgradeSubscription = useCallback((userId, planCode) => {
    return dispatch(upgradeSubscription({ userId, planCode }));
  }, [dispatch]);

  const handleCancelSubscription = useCallback((userId) => {
    return dispatch(cancelSubscription(userId));
  }, [dispatch]);

  const handleCancelBillingSubscription = useCallback((stripeSubId, userId) => {
    return dispatch(cancelBillingSubscription({ stripeSubId, userId }));
  }, [dispatch]);

  const handleSetBillingAutoRenew = useCallback((stripeSubId, enabled) => {
    return dispatch(setBillingAutoRenew({ stripeSubId, enabled }));
  }, [dispatch]);

  // Feature configuration
  const loadFeatureConfig = useCallback(() => {
    dispatch(fetchFeatureConfig());
  }, [dispatch]);

  const handleUpdateFeatureConfig = useCallback((config) => {
    return dispatch(updateFeatureConfig(config));
  }, [dispatch]);

  // Admin user management
  const loadAdminUsers = useCallback(() => {
    dispatch(fetchAdminUsers());
  }, [dispatch]); // Only depend on dispatch which is stable

  // Clear errors
  const clearSubscriptionErrors = useCallback(() => {
    dispatch(clearErrors());
  }, [dispatch]);

  // Clear subscription data
  const clearSubscriptionData = useCallback(() => {
    dispatch(clearSubscription());
  }, [dispatch]);

  // Load initial data
  useEffect(() => {
    // Don't automatically load plans - let components decide when to load them
    // This prevents infinite loops when components call loadPlans in their own useEffect
    // loadPlans();
  }, []);

  return {
    // State
    plans,
    currentSubscription,
    currentQuota,
    featureConfig,
    loading,
    error,
    adminUsers,
    adminLoading,
    adminError,
    billingLoading,
    billingError,
    subscriptionRefreshTrigger,
    
    // Actions
    loadPlans,
    loadUserSubscription,
    loadUserQuota,
    hasActiveSubscription,
    loadSubscriptionIfExists,
    loadSubscriptionForManagement,
    refreshSubscriptionData,
    smartRefreshSubscriptionData,
    shouldLoadSubscription,
    handleChangePlan,
    handleStartFreeSubscription,
    handleStartBillingSubscription,
    handleStartTrial,
    handleUpgradeSubscription,
    handleCancelSubscription,
    handleCancelBillingSubscription,
    handleSetBillingAutoRenew,
    loadFeatureConfig,
    handleUpdateFeatureConfig,
    loadAdminUsers,
    clearSubscriptionErrors,
    clearSubscriptionData,
    updateQuotaWithSubscriptionData,
    updateCreditsUsageData,
    triggerSubscriptionRefresh,
    syncQuotaWithSubscription,
    resetQuotaToFree
  };
};
